import { SubscribeClosed } from './errors.js';
import type { AggregateChange, AuthProvider } from './types.js';

export interface SubscribeDeps {
  baseUrl: string;
  auth: AuthProvider;
  eventSourceFactory: (url: string) => EventSourceLike;
  // Shared monotonic nonce source (W1); the SSE handshake signs once at open.
  nextNonce: () => string;
}

export interface SubscribeOptions {
  signal?: AbortSignal;
}

export interface EventSourceLike {
  addEventListener(type: 'message', listener: (ev: { data: unknown }) => void): void;
  addEventListener(type: 'error', listener: (ev: unknown) => void): void;
  addEventListener(type: 'open', listener: () => void): void;
  close(): void;
}

interface ChangeWireJson {
  aggregate_id: unknown;
  operation: unknown;
  from_aggregate_version?: unknown;
  to_aggregate_version?: unknown;
  keep_from_aggregate_version?: unknown;
}

const VALID_OPERATIONS = new Set(['write', 'create', 'trim', 'delete']);

function parseChange(raw: string): AggregateChange | undefined {
  if (raw.length === 0 || raw[0] !== '{') return undefined;
  let parsed: ChangeWireJson;
  try {
    parsed = JSON.parse(raw) as ChangeWireJson;
  } catch {
    return undefined;
  }
  if (typeof parsed.aggregate_id !== 'string' || parsed.aggregate_id.length === 0) return undefined;
  if (typeof parsed.operation !== 'string' || !VALID_OPERATIONS.has(parsed.operation)) return undefined;
  const change: AggregateChange = {
    aggregateId: parsed.aggregate_id,
    operation: parsed.operation as AggregateChange['operation'],
  };
  if (typeof parsed.from_aggregate_version === 'number') change.fromVersion = parsed.from_aggregate_version;
  if (typeof parsed.to_aggregate_version === 'number') change.toVersion = parsed.to_aggregate_version;
  if (typeof parsed.keep_from_aggregate_version === 'number') change.keepFromVersion = parsed.keep_from_aggregate_version;
  return change;
}

// SSE auth is signed ONCE at stream open; native EventSource auto-reconnects with the same
// URL so the nonce ages naturally. The gateway exempts SSE handshakes from anti-replay
// (DESIGN.md §Anti-replay) so this is the contract, not a bug. JWTs that expire mid-stream
// will eventually 401 the reconnect; the SDK on top should rebuild the subscription.
export async function* subscribe(
  deps: SubscribeDeps,
  type: string,
  ids: string[],
  options: SubscribeOptions = {},
): AsyncGenerator<AggregateChange, void, unknown> {
  if (type.length === 0) throw new Error('subscribe type must not be empty');
  if (ids.length === 0) throw new Error('subscribe requires at least one aggregate id');
  if (ids.length > 256) throw new Error('subscribe accepts at most 256 aggregate ids per stream');
  for (const id of ids) {
    if (id.length === 0) throw new Error('subscribe aggregate ids must not be empty');
  }

  const nonce = deps.nextNonce();
  const creds = await deps.auth(nonce);

  const params = new URLSearchParams();
  if (ids.length > 1) {
    params.set('aggregates', ids.join(','));
  }
  params.set('public_key', creds.publicKey);
  params.set('nonce', nonce);
  params.set('signature', creds.signature);
  if (creds.bearerToken !== undefined) params.set('token', creds.bearerToken);

  const url = ids.length === 1
    ? `${deps.baseUrl}/api/v1/${encodeURIComponent(type)}/${encodeURIComponent(ids[0]!)}/subscribe?${params.toString()}`
    : `${deps.baseUrl}/api/v1/${encodeURIComponent(type)}/subscribe?${params.toString()}`;

  if (options.signal?.aborted === true) return;

  const es = deps.eventSourceFactory(url);

  const buffer: AggregateChange[] = [];
  let waitingResolve: (() => void) | undefined;
  let aborted = false;

  const wake = (): void => {
    const r = waitingResolve;
    if (r !== undefined) {
      waitingResolve = undefined;
      r();
    }
  };

  es.addEventListener('message', (ev) => {
    const raw = typeof ev.data === 'string' ? ev.data : '';
    const change = parseChange(raw);
    if (change === undefined) return;
    buffer.push(change);
    wake();
  });

  // Native EventSource emits 'error' on EVERY reconnect attempt; those are the runtime's
  // job and stay swallowed. The exception (W4/O10): readyState CLOSED means the
  // EventSource permanently failed — per spec a non-200 response kills it with NO
  // auto-reconnect (gateway SSE-cap 503, 401 on aged credentials at reconnect, 403 on
  // revocation). Detect it and throw out of the generator so the layer above can rebuild
  // the subscription with a fresh nonce; swallowing it leaves the consumer awaiting a
  // dead stream forever. EventSourceLike doesn't require readyState (test fakes); absent
  // means we can't detect death and behave as before.
  let fatallyClosed = false;
  const EVENTSOURCE_CLOSED = 2;
  es.addEventListener('error', () => {
    if ((es as { readyState?: number }).readyState === EVENTSOURCE_CLOSED) {
      fatallyClosed = true;
      wake();
    }
  });

  // Catch-up on reconnect: the gateway streams changes from connect time only, so anything
  // committed while the connection was down produced no message. On every open AFTER the
  // first, poke each subscribed aggregate with a synthetic write so consumers re-pull
  // (a pull with nothing new is a no-op).
  let everOpened = false;
  es.addEventListener('open', () => {
    if (!everOpened) {
      everOpened = true;
      return;
    }
    for (const id of ids) buffer.push({ aggregateId: id, operation: 'write' });
    wake();
  });

  const abortListener = (): void => {
    aborted = true;
    wake();
  };
  if (options.signal !== undefined) {
    options.signal.addEventListener('abort', abortListener, { once: true });
    // Re-check after registration in case the signal fired between the initial check above
    // and the listener install (rare but real).
    if (options.signal.aborted) {
      aborted = true;
    }
  }

  try {
    while (!aborted) {
      while (buffer.length > 0) {
        const next = buffer.shift()!;
        yield next;
        if (aborted) return;
      }
      if (aborted) break;
      // Drain buffered changes first, THEN report death — they arrived before the close.
      if (fatallyClosed) throw new SubscribeClosed(url);
      await new Promise<void>((resolve) => { waitingResolve = resolve; });
    }
  } finally {
    if (options.signal !== undefined) options.signal.removeEventListener('abort', abortListener);
    es.close();
  }
}
